
import React, { useState, useEffect, useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import { validateNewPassword } from '@ecp/common/src/utils/InputValidateUtils';
import moTheme from '@ecp/common/src/style/theme/mo';
import { FlexBoxColumn, FlexBox } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { TextBox } from '@ecp/common/src/text/TextBox';
import { PageButton } from '@ecp/common/src/components/button/mo/Button';
import { MAX_PASSWORD_INPUT_LENGTH, MIN_PASSWORD_INPUT_LENGTH } from '@ecp/common/src/const/input';

import { userInfoState } from '@mo-recoil/common/user/atom';
import { useAlertDialog, useConfirmDialog } from '@mo-hooks/common/useModal';
import authApi from '@mo-apis/common/authApi';
import PasswordInput from '@mo-components/member/input/PasswordInput';

import OtpVerifyPopup from '@mo-pages/member/login/popup/OtpVerifyPopup';
import OtpVerifyInputPopup from '@mo-pages/member/login/popup/OtpVerifyInputPopup';

import { VERIFY_TYPE, SEND_VIA } from '@ecp/mo/src/const/member/memberConst';
import { getAccessToken } from '@ecp/common/src/utils/utils';

const Wrapper = styled(FlexBoxColumn)`
  align-items: flex-start;
  width: 100%;
`;

const PasswordLabel = styled(TextBox)`
  font-size: ${moTheme.font.size.$14};
  font-weight: ${moTheme.font.weight.medium};
  width: 100%;
`;

const screenText = {
  recovery: {
    passwordInputAlertMsg: '임시 비밀번호를 입력해주세요.',
    passwordNotMatchedAlertMsg: '현재 비밀번호가 일치하지 않습니다.',
    changePasswordAlertMsg: '입력하신 정보로 비밀번호를\n변경하시겠습니까?',
    passwordInputLabel: '임시비밀번호',
    passwordInputPlaceholder: '발급받은 임시 비밀번호를 입력해주세요.',
    reNewPasswordInputPlaceholder: '비밀번호 확인 입력',
    cancelButtonText: '취소',
    confirmButtonText: '비밀번호 변경',
  },
  mypage: {
    passwordInputAlertMsg: '현재 비밀번호를 입력해주세요.',
    passwordNotMatchedAlertMsg: '현재 비밀번호가 일치하지 않습니다.',
    changePasswordAlertMsg: '비밀번호를 변경하시겠습니까?',
    passwordInputLabel: '현재비밀번호',
    passwordInputPlaceholder: '현재 비밀번호를 입력해주세요.',
    reNewPasswordInputPlaceholder: '비밀번호 확인',
    cancelButtonText: '취소',
    confirmButtonText: '변경',
  },
  over90days: {
    passwordInputAlertMsg: '현재 비밀번호를 입력해주세요.',
    passwordNotMatchedAlertMsg: '현재 비밀번호가 일치하지 않습니다.',
    changePasswordAlertMsg: '비밀번호를 변경하시겠습니까?',
    passwordInputLabel: '현재비밀번호',
    passwordInputPlaceholder: '현재 비밀번호를 입력해주세요.',
    reNewPasswordInputPlaceholder: '비밀번호 확인',
    cancelButtonText: '90일 후 변경하기',
    confirmButtonText: '지금 변경하기',
  },
};

const ChangePassword = ({ updatePassword, onCancelButtonClick, userId, type = 'mypage', otpState }) => {
  const initUserInfo = {
    userId: '',
    siteNumber: '',
    cellphoneNumber: '',
    email: ''
  };
  const userInfo = useRecoilValue(userInfoState);
  const { showAlertDialog } = useAlertDialog();
  const { showConfirmDialog } = useConfirmDialog();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [reNewPassword, setReNewPassword] = useState('');

  const [userOtpInfo, setUserOtpInfo] = useState({ ...initUserInfo });
  const [openOtpVerify, setOpenOtpVerify] = useState(false);
  const [openOtpVerifyInput, setOpenOtpVerifyInput] = useState(false);
  const [authType, setAuthType] = useState(null);
  const [verifyType] = useState(VERIFY_TYPE.ETC);

  const otpPopupMessage = '회원님의 정보를 안전하게 보호하기 위해 등록된 휴대전화 또는 이메일로 추가 인증이 필요합니다.';

  useEffect(() => {
    // mypage에서 온 경우가 아니거나 2차인증 검증 api 호출 응답값이 true 일때
    // 2차 인증 페이지를 보여주지 않음
    if (type !== 'mypage' || otpState === true) {
      setOpenOtpVerify(false);
      setOpenOtpVerifyInput(false);
    }
    // mypage에서 온 경우 이고 2차인증 검증 api 호출 응답값이 false 일때
    // 2차 인증 페이지를 보여줌
    if (otpState === false && type === 'mypage') {
      setOpenOtpVerify(true);
    }
  }, [otpState, type]);
  
  useEffect(() => {
    (async () => {
      if (userInfo.userId != '' && userInfo.siteNumber != '' && type != 'recovery') {
        const { result } = await authApi.getOtpMethodInfo(getAccessToken());
        if (result) {
          setUserOtpInfo((prev) => ({
            ...prev,
            userId: userInfo.userId,
            siteNumber: userInfo.siteNumber,
            cellphoneNumber: result.cellphoneNumber,
            email: result.email
          }));
        }
      }
    })();
  }, [userId, userInfo]);

  const handleChangePassword = async () => {
    if (!(await isValidPassword())) return;

    showConfirmDialog(screenText[type].changePasswordAlertMsg, async () => await updatePassword(password, newPassword));
  };

  const confirmCurrentPassword = async () => {
    const {
      data: { result },
    } = await authApi.confirmPassword({ loginId: userId, password });
    return result?.matched;
  };

  const isValidPassword = async () => {
    if (!password || password.trim() === '') {
      showAlertDialog(screenText[type].passwordInputAlertMsg);
      return false;
    }

    const isMatchedCurrentPassword = await confirmCurrentPassword();
    if (!isMatchedCurrentPassword) {
      showAlertDialog(screenText[type].passwordNotMatchedAlertMsg);
      return false;
    }

    const errMessage = validateNewPassword(newPassword, reNewPassword, userId || userInfo.userId);
    if (errMessage) {
      showAlertDialog(errMessage);
      return false;
    }

    if (password === newPassword) {
      showAlertDialog('현재 비밀번호와 다르게 입력해주세요.');
      return false;
    }

    return true;
  };

  const handleOtpVerifyPopupClose = useCallback(() => {
    navigate("/my-page");
  }, [userOtpInfo]);

  const handleOtpVerifyInputPopupClose = useCallback(() => {
    navigate("/my-page");
  }, [userOtpInfo]);

  const handleOtpVerifyPopupConfirm = (selectedAuthType) => {
    setAuthType(selectedAuthType);
    setOpenOtpVerify(false);
    setOpenOtpVerifyInput(true);
  };

  const handleOtpVerifyInputPopupConfirm = useCallback(
    async (data) => {
      const result = authType === SEND_VIA.EMAIL ?
        await authApi.certifyEmail({
          userId: userOtpInfo.userId,
          siteNumber: userOtpInfo.siteNumber,
          certKey: data.certKey,
        })
        : await authApi.certifyCellphoneNumber({
          userId: userOtpInfo.userId,
          siteNumber: userOtpInfo.siteNumber,
          certKey: data.certKey,
        });

      if (!result?.code) {
        showAlertDialog('인증 성공하였습니다.', async () => {
          setOpenOtpVerifyInput(false);
        });
      } else {
        switch (result.code) {
          case 'AUTH_0002', 'AUTH_0008':
            showAlertDialog('인증번호가 일치하지 않습니다.');
            break;
          case 'AUTH_0006':
            showAlertDialog('인증번호 시도 횟수를 초과했습니다.\n인증번호를 새로 발급하여 진행해주세요.');
            break;
          default:
            showAlertDialog('인증에 실패했습니다.');
        }
      }
    },
    [userOtpInfo.userId, showAlertDialog, authType, otpState]
  );

  return (
    <Wrapper>
      <FlexBoxColumn width="100%">
        <PasswordLabel>{screenText[type].passwordInputLabel}</PasswordLabel>
        <Spacing top={10} />
        <PasswordInput
          value={password}
          placeholder={screenText[type].passwordInputPlaceholder}
          onChange={(e) => setPassword(e.target.value)}
          styleProps={{ height: '46px' }}
        />
      </FlexBoxColumn>
      <Spacing top={20} />
      <FlexBoxColumn width="100%">
        <PasswordLabel>신규비밀번호</PasswordLabel>
        <Spacing top={10} />
        <PasswordInput
          value={newPassword}
          placeholder={`영문(대소문자 구분),숫자,특수기호 혼합 ${MIN_PASSWORD_INPUT_LENGTH}~${MAX_PASSWORD_INPUT_LENGTH}자리`}
          onChange={(e) => setNewPassword(e.target.value)}
          styleProps={{ height: '46px' }}
        />
      </FlexBoxColumn>
      <Spacing top={20} />
      <FlexBoxColumn width="100%">
        <PasswordLabel>신규비밀번호 확인</PasswordLabel>
        <Spacing top={10} />
        <PasswordInput
          value={reNewPassword}
          placeholder={screenText[type].reNewPasswordInputPlaceholder}
          onChange={(e) => setReNewPassword(e.target.value)}
          styleProps={{ height: '46px' }}
        />
      </FlexBoxColumn>
      <Spacing top={30} />
      <FlexBox width="100%" justify-content="center">
        <PageButton type="Secondary" width="156px" onClick={onCancelButtonClick}>
          {screenText[type].cancelButtonText}
        </PageButton>
        <Spacing left={8} />
        <PageButton width="156px" onClick={handleChangePassword}>
          {screenText[type].confirmButtonText}
        </PageButton>
      </FlexBox>
      <OtpVerifyPopup
        open={openOtpVerify}
        onClose={handleOtpVerifyPopupClose}
        onConfirm={handleOtpVerifyPopupConfirm}
        verifyType={verifyType}
        message={otpPopupMessage}
      />
      <OtpVerifyInputPopup
        open={openOtpVerifyInput}
        userOtpInfo={userOtpInfo}
        onClose={handleOtpVerifyInputPopupClose}
        onConfirm={handleOtpVerifyInputPopupConfirm}
        verifyType={verifyType}
        authType={authType}
      />
    </Wrapper>
  );
};

export default ChangePassword;

ChangePassword.propTypes = {
  updatePassword: PropTypes.func,
  onCancelButtonClick: PropTypes.func,
  userId: PropTypes.string,
  type: PropTypes.oneOf(['recovery', 'mypage', 'over90days']),
  otpState: PropTypes.bool,
};
